import { useSyncExternalStore } from 'react'

export type ThemeChoice = 'system' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

/** Same key as public/theme-init.js, which applies it before the first paint. */
export const THEME_STORAGE_KEY = 'arqam.theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === 'system' || value === 'light' || value === 'dark'
}

/** localStorage, or undefined where access throws (blocked storage, sandboxed frames). */
export function safeStorage(): Storage | undefined {
  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

/** The stored theme choice, or "system" when none, an unknown one, or blocked storage. */
export function readThemeChoice(storage: Pick<Storage, 'getItem'> | undefined): ThemeChoice {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY)
    return isThemeChoice(value) ? value : 'system'
  } catch {
    return 'system'
  }
}

/** Stores the choice; a storage error is ignored because the choice still applies now. */
export function writeThemeChoice(
  storage: Pick<Storage, 'setItem'> | undefined,
  choice: ThemeChoice
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // Not persisting is acceptable: the choice still applies to this page view.
  }
}

/** The theme in effect: an explicit choice wins, "system" follows the preference. */
export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): Theme {
  if (choice === 'system') {
    return prefersDark ? 'dark' : 'light'
  }
  return choice
}

function prefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

// One theme state for the whole page. Every useTheme() caller reads it through
// useSyncExternalStore, and only this module writes data-theme (after public/theme-init.js set
// it before the first paint), so two mounted switches can never disagree or override each other.
interface ThemeState {
  choice: ThemeChoice
  systemDark: boolean
}

const listeners = new Set<() => void>()
let state: ThemeState | undefined
let systemQuery: MediaQueryList | undefined

function currentState(): ThemeState {
  state ??= { choice: readThemeChoice(safeStorage()), systemDark: prefersDark() }
  return state
}

function update(next: Partial<ThemeState>): void {
  state = { ...currentState(), ...next }
  document.documentElement.setAttribute('data-theme', resolveTheme(state.choice, state.systemDark))
  for (const listener of listeners) {
    listener()
  }
}

function onSystemChange(event: MediaQueryListEvent): void {
  update({ systemDark: event.matches })
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  if (listeners.size === 1 && typeof window.matchMedia === 'function') {
    systemQuery = window.matchMedia(DARK_QUERY)
    systemQuery.addEventListener('change', onSystemChange)
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      systemQuery?.removeEventListener('change', onSystemChange)
      systemQuery = undefined
    }
  }
}

/** Stores the choice for this browser and applies it to the page. */
export function setThemeChoice(choice: ThemeChoice): void {
  writeThemeChoice(safeStorage(), choice)
  update({ choice })
}

/** Current theme choice, a setter that persists it, and the theme in effect. */
export function useTheme(): {
  choice: ThemeChoice
  setChoice: (choice: ThemeChoice) => void
  theme: Theme
} {
  const { choice, systemDark } = useSyncExternalStore(subscribe, currentState, currentState)
  return { choice, setChoice: setThemeChoice, theme: resolveTheme(choice, systemDark) }
}
